/**
 * Shared Hydration Utilities
 * 
 * These utilities transform raw server responses into normalized exercise data
 * suitable for rendering in both admin and student UIs.
 */

import { exerciseRegistry } from '../registry';
import type { ExerciseDefinition } from '../types';

// Import configs to ensure they're registered
import '../configs';

/**
 * Exercise details as received from the server API
 */
export interface ServerExerciseDetails {
  exerciseId?: string;
  exerciseType?: string;
  correction?: string | null;
  exerciseDetails?: Record<string, unknown>;
  title?: string | null;
  description?: string | null;
  maxScore?: number | null;
  passingScore?: number | null;
}

/**
 * Hydrated exercise content ready for UI consumption
 */
export interface HydratedExerciseContent {
  exerciseId: string;
  exerciseType: string;
  correction: string;
  title: string;
  description: string;
  maxScore: number | null;
  passingScore: number | null;
  details: Record<string, unknown>;
}

/**
 * Hydrate exercise content from server response.
 * Uses the exercise registry to get type-specific hydration logic.
 */
export function hydrateExerciseContent(serverData: ServerExerciseDetails): HydratedExerciseContent | null {
  const exerciseType = serverData.exerciseDetails?.type as string | undefined 
    ?? serverData.exerciseType 
    ?? 'MultipleChoice';
  
  const exerciseId = serverData.exerciseId;
  if (!exerciseId) {
    return null;
  }

  // Get type-specific hydrated details from the registry
  const hydratedDetails = exerciseRegistry.hydrateDetails(
    exerciseType,
    serverData.exerciseDetails ?? {}
  );

  return {
    exerciseId,
    exerciseType,
    correction: serverData.correction ?? '',
    title: serverData.title ?? '',
    description: serverData.description ?? '',
    maxScore: serverData.maxScore ?? null,
    passingScore: serverData.passingScore ?? null,
    details: {
      type: exerciseType,
      ...hydratedDetails,
    },
  };
}

/**
 * Build a normalized ExerciseDefinition from server response.
 * This is used for evaluation and submission.
 */
export function buildExerciseDefinition(serverData: ServerExerciseDetails): ExerciseDefinition | null {
  const exerciseType = serverData.exerciseDetails?.type as string | undefined 
    ?? serverData.exerciseType;
  
  const exerciseId = serverData.exerciseId;
  if (!exerciseId || !exerciseType) {
    return null;
  }

  const base = {
    exerciseId,
    title: serverData.title,
    description: serverData.description,
    correction: serverData.correction,
    maxScore: serverData.maxScore,
    passingScore: serverData.passingScore,
  };

  const details = serverData.exerciseDetails ?? {};

  switch (exerciseType) {
    case 'MultipleChoice': {
      const options = (details.options as any[] ?? []).map((option: any, index: number) => ({
        optionId: option?.optionId ?? option?.multipleChoiceOptionId ?? option?.id,
        label: option?.label ?? '',
        isCorrect: Boolean(option?.isCorrect),
        order: option?.order ?? index,
      }));
      return {
        ...base,
        type: 'MultipleChoice',
        question: (details.question as string) ?? '',
        options,
      };
    }

    case 'FillInBlank': {
      const sentences = (details.sentences as any[] ?? []).map((sentence: any, index: number) => ({
        sentenceId: sentence?.sentenceId,
        sentence: sentence?.sentence ?? '',
        correction: sentence?.correction ?? null,
        order: sentence?.order ?? index,
        blanks: (sentence?.blanks ?? []).map((blank: any, blankIndex: number) => ({
          blankId: blank?.blankId,
          position: blank?.position ?? blankIndex,
          answer: blank?.answer ?? '',
          correction: blank?.correction ?? null,
        })),
      }));
      return {
        ...base,
        type: 'FillInBlank',
        sentences,
      };
    }

    case 'SelectForBlank': {
      const sentences = (details.sentences as any[] ?? []).map((sentence: any, sentenceIndex: number) => ({
        sentenceId: sentence?.sentenceId,
        sentence: sentence?.sentence ?? '',
        correction: sentence?.correction ?? null,
        order: sentence?.order ?? sentenceIndex,
        blanks: (sentence?.blanks ?? []).map((blank: any, blankIndex: number) => ({
          blankId: blank?.blankId,
          position: blank?.position ?? blankIndex,
          options: (blank?.options ?? []).map((option: any, optionIndex: number) => ({
            optionId: option?.optionId,
            label: option?.label ?? '',
            isCorrect: Boolean(option?.isCorrect),
            order: option?.order ?? optionIndex,
          })),
        })),
      }));
      return {
        ...base,
        type: 'SelectForBlank',
        sentences,
      };
    }

    case 'Written': {
      const textType = (details.textType as string) ?? 'long';
      return {
        ...base,
        type: textType === 'long' ? 'LongAnswer' : 'ShortAnswer',
        format: (details.format as 'plain' | 'markdown' | 'html') ?? 'plain',
        textType: textType as 'long' | 'short',
      };
    }

    case 'Recorded':
      return {
        ...base,
        type: 'Recorded',
        maxDurationSec: (details.maxDurationSec as number) ?? null,
      };

    case 'Conversational': {
      let targetSkills: string[] | null = null;
      if (Array.isArray(details.targetSkills)) {
        targetSkills = details.targetSkills as string[];
      } else if (typeof details.targetSkills === 'string') {
        try {
          targetSkills = JSON.parse(details.targetSkills);
        } catch {
          targetSkills = null;
        }
      }
      return {
        ...base,
        type: 'Conversational',
        theme: (details.theme as string) ?? '',
        maxDurationSec: (details.maxDurationSec as number) ?? 300,
        systemPrompt: (details.systemPrompt as string) ?? null,
        targetSkills,
      };
    }

    case 'Matching': {
      // Handle both `pairs` array and `prompts` array formats
      let pairs: any[] = [];
      if (Array.isArray(details.pairs)) {
        pairs = details.pairs.map((pair: any, index: number) => ({
          pairId: pair?.pairId ?? `${index}`,
          promptId: pair?.promptId ?? pair?.pairId ?? `${index}`,
          promptLabel: pair?.promptLabel ?? '',
          optionId: pair?.optionId ?? '',
          optionLabel: pair?.optionLabel ?? '',
          order: pair?.order ?? index,
        }));
      } else if (Array.isArray(details.prompts)) {
        pairs = (details.prompts as any[]).map((prompt: any, index: number) => {
          const correctOption = (prompt.options ?? []).find((option: any) => option.isCorrect);
          return {
            pairId: prompt.promptId ?? `${index}`,
            promptId: prompt.promptId ?? `${index}`,
            promptLabel: prompt.promptText ?? '',
            optionId: correctOption?.optionId ?? '',
            optionLabel: correctOption?.optionText ?? '',
            order: prompt.order ?? index,
          };
        });
      }
      return {
        ...base,
        type: 'Matching',
        pairs,
      };
    }

    default:
      return null;
  }
}

/**
 * Infer the exercise subtype from a type string.
 * Maps server-side types to UI exercise subtypes.
 */
export function inferExerciseSubtype(type?: string): string {
  switch (type) {
    case 'MultipleChoice':
      return 'MultipleChoice';
    case 'Recorded':
      return 'Recorded';
    case 'Written':
      return 'LongAnswer';
    case 'FillInBlank':
      return 'FillInBlank';
    case 'SelectForBlank':
      return 'SelectForBlank';
    case 'Conversational':
      return 'Conversational';
    case 'Matching':
      return 'Matching';
    case 'Scramble':
      return 'Scramble';
    case 'FileUpload':
      return 'FileUpload';
    default:
      return 'MultipleChoice';
  }
}

/**
 * Build matching pairs from exercise details.
 * Handles both `pairs` and `prompts` input formats.
 */
export function buildMatchingPairs(exerciseDetails: Record<string, unknown>): Array<{
  pairId?: string;
  promptId?: string;
  promptLabel: string;
  optionId?: string;
  optionLabel: string;
  order?: number;
}> | undefined {
  if (exerciseDetails?.type !== 'Matching') {
    return undefined;
  }

  // If pairs are provided directly (from server), use them
  if (Array.isArray(exerciseDetails.pairs)) {
    return (exerciseDetails.pairs as any[]).map((pair: any, index: number) => ({
      pairId: pair?.pairId,
      promptId: pair?.promptId ?? pair?.pairId,
      promptLabel: pair?.promptLabel ?? '',
      optionId: pair?.optionId,
      optionLabel: pair?.optionLabel ?? '',
      order: pair?.order ?? index,
    }));
  }

  // Fallback: if prompts are provided (from admin editor), extract pairs
  if (Array.isArray(exerciseDetails.prompts)) {
    return (exerciseDetails.prompts as any[]).map((prompt: any, index: number) => {
      const correctOption = (prompt.options ?? []).find((option: any) => option.isCorrect);
      return {
        pairId: prompt.promptId,
        promptId: prompt.promptId,
        promptLabel: prompt.promptText ?? '',
        optionId: correctOption?.optionId,
        optionLabel: correctOption?.optionText ?? '',
        order: prompt.order ?? index,
      };
    });
  }

  return undefined;
}
